import imageCompression from "browser-image-compression";

/** Kích thước thumbnail chuẩn (ô list/card ~76px, retina + nén nhẹ lưu DB). */
export const LESSON_THUMB = {
  width: 320,
  height: 320,
  aspect: 1,
  /** Mục tiêu dung lượng sau nén FE (~40KB) — lưu BYTEA trong Postgres */
  maxSizeMB: 0.04,
  /** Cho phép chọn ảnh gốc lớn; app sẽ crop + nén */
  maxInputBytes: 12 * 1024 * 1024,
  accept: "image/jpeg,image/jpg,image/png,image/webp,image/gif",
  note:
    "Khuyến nghị ảnh vuông 320×320 px (tỷ lệ 1:1). Có thể chọn ảnh khác kích thước — hệ thống cắt giữa khung và nén (~40KB) rồi lưu trực tiếp trong DB (không dùng GCS).",
};

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Không đọc được ảnh"));
    };
    img.src = url;
  });
}

/**
 * Cắt cover giữa khung về đúng tỷ lệ (mặc định 1:1) rồi vẽ ra canvas kích thước đích.
 */
export async function cropCoverToCanvas(
  file,
  { width = LESSON_THUMB.width, height = LESSON_THUMB.height } = {}
) {
  const img = await loadImageFromFile(file);
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  if (!srcW || !srcH) throw new Error("Ảnh không hợp lệ");

  const targetRatio = width / height;
  const srcRatio = srcW / srcH;
  let sx = 0;
  let sy = 0;
  let sw = srcW;
  let sh = srcH;

  if (srcRatio > targetRatio) {
    // Ảnh rộng hơn → cắt 2 bên
    sw = Math.round(srcH * targetRatio);
    sx = Math.round((srcW - sw) / 2);
  } else if (srcRatio < targetRatio) {
    // Ảnh cao hơn → cắt trên/dưới
    sh = Math.round(srcW / targetRatio);
    sy = Math.round((srcH - sh) / 2);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Trình duyệt không hỗ trợ canvas");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
  return canvas;
}

function canvasToBlob(canvas, type = "image/jpeg", quality = 0.82) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error("Không tạo được ảnh"));
        else resolve(blob);
      },
      type,
      quality
    );
  });
}

/**
 * Chuẩn bị thumbnail: crop cover 400×400 + nén nhẹ.
 * @returns {{ file: File, previewUrl: string, bytes: number }}
 */
export async function prepareLessonThumbnail(file) {
  if (!file) throw new Error("Chưa chọn ảnh");
  if (!/^image\//i.test(file.type || "")) {
    throw new Error("Vui lòng chọn file ảnh (JPEG, PNG, WEBP…)");
  }
  if (file.size > LESSON_THUMB.maxInputBytes) {
    throw new Error("Ảnh quá lớn (tối đa 12MB). Hãy chọn ảnh nhẹ hơn.");
  }

  const canvas = await cropCoverToCanvas(file, {
    width: LESSON_THUMB.width,
    height: LESSON_THUMB.height,
  });
  let blob = await canvasToBlob(canvas, "image/jpeg", 0.85);
  let outFile = new File([blob], "lesson-thumb.jpg", { type: "image/jpeg" });

  if (outFile.size > LESSON_THUMB.maxSizeMB * 1024 * 1024) {
    const compressed = await imageCompression(outFile, {
      maxSizeMB: LESSON_THUMB.maxSizeMB,
      maxWidthOrHeight: LESSON_THUMB.width,
      useWebWorker: true,
      fileType: "image/jpeg",
      initialQuality: 0.75,
    });
    outFile = new File([compressed], "lesson-thumb.jpg", {
      type: "image/jpeg",
    });
  }

  const previewUrl = URL.createObjectURL(outFile);
  return { file: outFile, previewUrl, bytes: outFile.size };
}

export function formatThumbBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** File → data URL base64 (gửi kèm create/update lesson, lưu BYTEA trên BE). */
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Không đọc được ảnh"));
    reader.readAsDataURL(file);
  });
}
